import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import {
  fetchLocation,
  parseLocationFromString,
  updateLocation,
  updateMapCenter,
} from "../../actions/currentLocation";
import {
  fetchResults,
  updateSelectedCuisines,
} from "../../actions/restaurants";
import { setMinLeafCount, setVerificationTier } from "../../actions/filters";
import { LIST_VIEW } from "../../actions/types";
import { getLatLong } from "../../util/geoUtils";
import { getRestaurantCuisines } from "../../util/getRestaurantCuisines";

import CuisinePicker from "../common/CuisinePicker";
import FilterChips from "./FilterChips";
import useEvent from "../../hooks/useEvent";

const SearchBar = ({
  fetchResultsAction,
  currentLocation,
  mapCenter,
  updateLocation,
  updateMapCenter,
  fetchLocation,
  selectedCuisines,
  updateSelectedCuisines,
  minLeafCount,
  verificationTier,
  setMinLeafCount,
  setVerificationTier,
  view,
  restaurants,
  adHocSearchActive,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFormData = {
    cuisines: [],
    latLngString: "",
    keyword: "", // Initialize keyword to empty string, not undefined
    showCurrentLocationOption: false,
  };

  const [otherCuisineText, setOtherCuisineText] = useState("");

  const [formData, setFormData] = useState(initialFormData);
  const { cuisines, latLngString, keyword, showCurrentLocationOption } =
    formData;
  const [stringifiedCuisines, setStringifiedCuisines] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [reducedState, setReducedState] = useState({
    keyword,
    stringifiedCuisines,
    lat: currentLocation.lat,
    lng: currentLocation.lng,
    mapLat: mapCenter.lat,
    mapLng: mapCenter.lng,
    radius: currentLocation.radius,
  });

  const searchTools = useRef(null);
  const searchSourceRef = useRef("pageload");

  // Get event context if on event route
  const eventContext = useEvent();
  const event = eventContext?.event;
  // Only use eventId if we're actually on an event route (check pathname)
  // This prevents event filtering when navigating to main page
  const isEventRoute = location.pathname.match(
    /^\/events\/([^/]+?)(?:\/embed)?\/?$/
  );
  const eventId = (isEventRoute && event?._id) || null;
  

  const [open, setOpen] = useState(false);

  const blur = (e) => {
    if (e.relatedTarget && searchTools.current.contains(e.relatedTarget)) {
      return;
    }
    setOpen(false);
  };

  useEffect(function fetchCuisinesOnMount() {
    async function fetchCuisines() {
      const response = await getRestaurantCuisines();
      setFormData((formData) => ({ ...formData, cuisines: response }));
    }
    fetchCuisines();
  }, []);

  // Sync keyword from URL query params (handles both setting and clearing)
  // URL is the source of truth for keywords
  useEffect(function syncFiltersFromUrl() {
    const searchParams = new URLSearchParams(location.search);
    const keywordFromUrl = searchParams.get("keywords");
    // Normalize: null or empty string both become null for comparison
    const normalizedKeyword = keywordFromUrl || null;

    // Always update from URL - this is the source of truth
    // Use functional update to avoid stale closure issues
    setReducedState((prev) => {
      const prevNormalized = prev.keyword || null;
      if (normalizedKeyword !== prevNormalized) {
        return {
          ...prev,
          keyword: normalizedKeyword,
        };
      }
      return prev; // No change needed
    });

    // Also update formData so the input shows the keyword
    setFormData((prev) => ({
      ...prev,
      keyword: keywordFromUrl || "",
    }));

    // Sync cuisine filter from URL
    const cuisineFromUrl = searchParams.get("cuisine");
    if (cuisineFromUrl) {
      const cuisineObj = {};
      cuisineFromUrl.split(",").filter(Boolean).forEach((c) => {
        cuisineObj[c] = c;
      });
      // Only update if different to avoid infinite loops
      const currentCuisineStr = Object.values(selectedCuisines).sort().join(",");
      const urlCuisineStr = Object.values(cuisineObj).sort().join(",");
      if (currentCuisineStr !== urlCuisineStr) {
        updateSelectedCuisines(cuisineObj);
      }
    } else if (Object.keys(selectedCuisines).length > 0 && searchParams.has("lat")) {
      // URL has no cuisine param but Redux has cuisines and we have a search URL — clear
      // Only clear if we're on a URL with lat/lng (i.e., an actual search URL)
      updateSelectedCuisines({});
    }

    // Sync minLeafCount from URL
    const minLeafFromUrl = searchParams.get("minLeaf");
    const parsedMinLeaf = minLeafFromUrl ? parseInt(minLeafFromUrl) : null;
    if (parsedMinLeaf !== minLeafCount) {
      setMinLeafCount(parsedMinLeaf);
    }

    // Sync verificationTier from URL
    const tierFromUrl = searchParams.get("tier");
    const parsedTier = tierFromUrl || null;
    if (parsedTier !== verificationTier) {
      setVerificationTier(parsedTier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(function stringifyCuisinesWhenSelectionChanges() {
    setStringifiedCuisines(Object.values(selectedCuisines).join(","));
  }, [selectedCuisines]);

  useEffect(function syncReducedStateFromMapCenter() {
    searchSourceRef.current = "pan";
    setReducedState((reducedState) => ({
      ...reducedState,
      mapLat: mapCenter.lat,
      mapLng: mapCenter.lng,
      radius: currentLocation.radius,
    }));
    // reducedState.keyword is intentionally excluded - we preserve it via functional setState, not update it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter.lat, mapCenter.lng, currentLocation.radius]);

  // Don't sync keyword from formData to reducedState automatically
  // Keyword should only be set in reducedState when form is submitted
  // This prevents the keyword from being overwritten when formData.keyword changes

  // Check if there are ad hoc restaurants in the results
  const hasAdHocResults = restaurants?.some(
    (r) => r.metadata?.adHocSearch === true
  );

  // Hydrate from SSR data on first render (skip initial API fetch)
  const ssrConsumed = useRef(false);

  useEffect(function fetchResultsWhenSearchParamsChange() {
    // Skip search if ad hoc results are present
    // User must clear ad hoc results before performing a new search
    if (hasAdHocResults) {
      return;
    }

    // If SSR data is available, use it instead of fetching
    if (window.__SSR_DATA__?.restaurants && !ssrConsumed.current) {
      ssrConsumed.current = true;
      // Dispatch SSR restaurants via a synthetic fetch that returns cached data
      // The fetchResultsAction won't match this pattern, so we use store directly
      import("../../store").then(({ default: store }) => {
        store.dispatch({
          type: "RESULTS_RECEIVED",
          payload: window.__SSR_DATA__.restaurants,
        });
        delete window.__SSR_DATA__;
      });
      return;
    }

    if (!currentLocation.fetchLocation) {
      // Read all search params directly from URL (source of truth)
      // This avoids a race condition where reducedState.keyword is stale
      // when location.search and reducedState.keyword both trigger this effect
      const urlParams = new URLSearchParams(location.search);
      const keywordToUse = urlParams.get("keywords") || null;

      // Read include param from URL (curated restaurant IDs for lists)
      const includeIds = urlParams.get("include") || null;
      const listName = urlParams.get("listName") || null;

      const source = searchSourceRef.current;
      searchSourceRef.current = "pan"; // Reset after capturing

      fetchResultsAction(
        {
          lat: reducedState.mapLat,
          lng: reducedState.mapLng,
          radius: reducedState.radius,
        },
        reducedState.selectedCuisines,
        keywordToUse,
        eventId,
        includeIds,
        source,
        listName
      );
    }
    // eventContext is intentionally excluded from dependencies to prevent
    // unnecessary rerenders when the context object reference changes.
    // We only need eventId for the search, which is already in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLocation.fetchLocation,
    fetchLocation,
    fetchResultsAction,
    reducedState.mapLat,
    reducedState.mapLng,
    reducedState.selectedCuisines,
    reducedState.radius,
    location.search,
    eventId,
  ]);

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const onSubmit = async (e) => {
    e.preventDefault();
    searchSourceRef.current = "search";
    const keywordToSave = keyword || null; // Convert empty string to null
    // setLocation will update the keyword in reducedState before triggering the search
    setLocation(latLngString, keywordToSave);
  };
  const onFocus = () =>
    setFormData({ ...formData, showCurrentLocationOption: true });
  const onBlur = (e) => {
    e.preventDefault();
    setFormData({ ...formData, showCurrentLocationOption: false });
  };
  // Helper to sync all filter state to URL params
  const syncFiltersToParams = (params) => {
    // Cuisine
    const cuisineStr = Object.values(selectedCuisines).filter(Boolean).join(",");
    if (cuisineStr) {
      params.set("cuisine", cuisineStr);
    } else {
      params.delete("cuisine");
    }
    // Leaf count
    if (minLeafCount) {
      params.set("minLeaf", minLeafCount.toString());
    } else {
      params.delete("minLeaf");
    }
    // Verification tier
    if (verificationTier) {
      params.set("tier", verificationTier);
    } else {
      params.delete("tier");
    }
  };

  const setLocation = async (zipcode, keywordOverride = undefined) => {
    setIsSearching(true);
    try {
      // Use keywordOverride if provided, otherwise use current reducedState.keyword
      // The URL sync useEffect will update reducedState.keyword from the URL (source of truth)
      const keywordToUse = keywordOverride !== undefined ? keywordOverride : reducedState.keyword;

      // Read current URL params to preserve other query params (like zoom, cuisine, etc.)
      const currentParams = new URLSearchParams(location.search);

      if (zipcode && zipcode !== "" && zipcode.length >= 5) {
        fetchLocation();
        const { lat, lng } = (await getLatLong(zipcode)).data;

        // Update location params
        currentParams.set('lat', lat.toString());
        currentParams.set('lng', lng.toString());

        // Update or remove keywords param
        if (keywordToUse) {
          currentParams.set('keywords', keywordToUse);
        } else {
          currentParams.delete('keywords');
        }

        // Sync filter state to URL
        syncFiltersToParams(currentParams);

        // Update URL with all params - MapResults will see this and call updateLocation
        // which will trigger the search useEffect with the updated keyword
        navigate(`?${currentParams.toString()}`);
        await updateLocation({ lat, lng });
      } else {
        // Update location params
        currentParams.set('lat', reducedState.mapLat.toString());
        currentParams.set('lng', reducedState.mapLng.toString());

        // Update or remove keywords param
        if (keywordToUse) {
          currentParams.set('keywords', keywordToUse);
        } else {
          currentParams.delete('keywords');
        }

        // Sync filter state to URL
        syncFiltersToParams(currentParams);

        // Update URL for bookmarkability — the search useEffect will fire
        // because location.search is in its dependency array
        navigate(`?${currentParams.toString()}`);
      }
    } finally {
      setIsSearching(false);
    }
  };
  const getUserLocation = () => {
    if (navigator.geolocation) {
      fetchLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Read current URL params to preserve other query params (like zoom, cuisine, etc.)
          const currentParams = new URLSearchParams(location.search);
          
          // Update location params
          currentParams.set('lat', latitude.toString());
          currentParams.set('lng', longitude.toString());
          
          // Update or remove keywords param
          if (reducedState.keyword) {
            currentParams.set('keywords', reducedState.keyword);
          } else {
            currentParams.delete('keywords');
          }
          
          // Sync filter state to URL
          syncFiltersToParams(currentParams);

          // Update URL with all params
          navigate(`?${currentParams.toString()}`);
          // updateLocation will update mapCenter, which triggers the search useEffect
          updateLocation({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.log("error getting position");
          console.log(error);
          if (error.message.includes("denied")) {
            alert("This requires location to be shared!");
          }
          fetchLocation(false);
        }
      );
    }
  };

  return (
    <div
      className={`search-tools-container flex-parent ${
        adHocSearchActive ? "ad-hoc-disabled" : ""
      }`}
      ref={searchTools}
      tabIndex={adHocSearchActive ? -1 : 0}
      onFocus={() => !adHocSearchActive && setOpen(true)}
      onBlur={blur}
      title={
        adHocSearchActive
          ? "Search is disabled while discovery search is running. Please wait for it to complete or cancel it."
          : ""
      }
    >
      <span
        className={`search-tools-style ${
          view === LIST_VIEW && !adHocSearchActive ? "none" : ""
        } ${open && !adHocSearchActive ? "none" : ""} ${
          adHocSearchActive ? "search-disabled" : ""
        }`}
        role="button"
        aria-label="Open search form"
        aria-disabled={adHocSearchActive}
        title={
          adHocSearchActive
            ? "Search is disabled while discovery search is active"
            : ""
        }
      >
        <i class="fas fa-magnifying-glass"></i> Search
      </span>
      <form
        className={`form-inline flex-parent search-tools-style absolute ${
          open ? "" : "none"
        }`}
        autoComplete="off"
        onSubmit={(e) => {
          if (adHocSearchActive) {
            e.preventDefault();
            return;
          }
          onSubmit(e);
        }}
      >
        <div className="flex-parent flex-grow">
          <div
            className="form-group form-control"
            onFocus={() => onFocus()}
            onBlur={(e) => onBlur(e)}
          >
            <input
              type="text"
              placeholder="Location"
              name="latLngString"
              value={latLngString}
              onChange={(e) => onChange(e)}
              className={"flex-text"}
              aria-label="Search location"
              disabled={adHocSearchActive}
            />
            <input
              className={`flex-fill inline-search-button ${
                isSearching ? "loading" : ""
              }`}
              value="&#xe521;"
              type="submit"
              aria-label="Search"
              disabled={isSearching || adHocSearchActive}
            ></input>
            <div
              className={
                "form-option " +
                (showCurrentLocationOption ? "visible" : "hidden")
              }
              role="button"
              tabIndex={0}
              aria-label="Use my current location"
              onMouseDown={() => {
                getUserLocation();
                setFormData({ ...formData, latLngString: "" });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  getUserLocation();
                  setFormData({ ...formData, latLngString: "" });
                }
              }}
            >
              <i className="fas fa-location-arrow" />
              <span className="vertical-center">&nbsp;Use my location</span>
            </div>
          </div>
          <input
            type="text"
            placeholder="Keyword"
            name="keyword"
            value={keyword}
            onChange={(e) => onChange(e)}
            className={"flex-text"}
            aria-label="Search keyword"
          />
          <div className={adHocSearchActive ? "disabled" : ""}>
            <CuisinePicker
              className="form-control"
              passedCuisines={cuisines}
              selectedCuisines={selectedCuisines}
              otherCuisineText={otherCuisineText}
              updateSelectedCuisines={updateSelectedCuisines}
              setOtherCuisineText={setOtherCuisineText}
            />
          </div>
        </div>
        <FilterChips />
      </form>
    </div>
  );
};
SearchBar.propTypes = {
  fetchResults: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
  currentLocation: state.currentLocation,
  mapCenter: state.mapCenter,
  selectedCuisines: state.selectedCuisines,
  minLeafCount: state.filters.minLeafCount,
  verificationTier: state.filters.verificationTier,
  view: state.results.view,
  restaurants: state.restaurant.restaurants,
  adHocSearchActive: state.restaurant.adHocSearchActive,
});
export default connect(mapStateToProps, {
  fetchResultsAction: fetchResults,
  updateLocation,
  updateMapCenter,
  parseLocationFromString,
  fetchLocation,
  updateSelectedCuisines: updateSelectedCuisines,
  setMinLeafCount,
  setVerificationTier,
})(SearchBar);
